import React, { useState, useEffect, useRef, useCallback } from 'react';
import './HowItWorks.css';

/**
 * HowItWorks — animated auto-advancing carousel for landing pages.
 *
 * Props:
 *   steps          — Step[] array (number, icon, title, description, visual)
 *   autoAdvanceMs  — interval in ms (default: 4000)
 */
function HowItWorks({ steps = [], autoAdvanceMs = 4000 }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isPaused = useRef(false);
  const intervalRef = useRef(null);

  const startInterval = useCallback(() => {
    if (steps.length === 0) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isPaused.current) {
        setCurrentSlide(prev => (prev + 1) % steps.length);
      }
    }, autoAdvanceMs);
  }, [steps.length, autoAdvanceMs]);

  useEffect(() => {
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, [startInterval]);

  const goToSlide = (index) => {
    const clamped = Math.max(0, Math.min(index, steps.length - 1));
    setCurrentSlide(clamped);
    startInterval(); // reset timer on manual navigation
  };

  const handlePrev = () => {
    goToSlide((currentSlide - 1 + steps.length) % steps.length);
  };

  const handleNext = () => {
    goToSlide((currentSlide + 1) % steps.length);
  };

  if (steps.length === 0) return null;

  return (
    <section className="how-it-works" id="how-it-works">
      <div className="section-header">
        <h2>How It Works</h2>
        <p>Get started in just a few simple steps</p>
      </div>

      <div
        className="how-it-works-carousel"
        onMouseEnter={() => { isPaused.current = true; }}
        onMouseLeave={() => { isPaused.current = false; }}
      >
        {/* Slides */}
        <div className="hiw-slides-wrapper">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`hiw-slide${i === currentSlide ? ' hiw-slide-active' : ''}${
                i < currentSlide ? ' hiw-slide-left' : i > currentSlide ? ' hiw-slide-right' : ''
              }`}
              aria-hidden={i !== currentSlide}
            >
              <div className="hiw-slide-inner">
                {/* Visual panel */}
                <div className="hiw-visual">
                  <div className="hiw-visual-emoji">{step.visual}</div>
                </div>

                {/* Content panel */}
                <div className="hiw-content">
                  <div className="hiw-step-number">{step.number}</div>
                  <div className="hiw-icon">
                    <i className={`fas ${step.icon}`}></i>
                  </div>
                  <h3 className="hiw-title">{step.title}</h3>
                  <p className="hiw-description">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / Next arrows */}
        <button
          className="hiw-arrow hiw-arrow-prev"
          onClick={handlePrev}
          aria-label="Previous step"
        >
          ‹
        </button>
        <button
          className="hiw-arrow hiw-arrow-next"
          onClick={handleNext}
          aria-label="Next step"
        >
          ›
        </button>

        {/* Navigation dots */}
        <div className="hiw-dots" role="tablist" aria-label="Slide navigation">
          {steps.map((_, i) => (
            <button
              key={i}
              className={`hiw-dot${i === currentSlide ? ' hiw-dot-active' : ''}`}
              onClick={() => goToSlide(i)}
              role="tab"
              aria-selected={i === currentSlide}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;
